// Section 12 and 22 (Health tab): a read-only overview for the view model. The triage for the
// coming season is the same `planCare` the bodies phase runs, fed with the draft's health-care
// policy, triage marks and job assignments, the healers' evaluated output and the medicine on
// hand. Last season's multiplier and care come from its report, as rolled (stream M3b).
import { config, type Config } from '../config';
import { emptySeasonReport } from '../core/state-init';
import {
  isWorking,
  lastCompletedReport,
  peopleSorted,
  quarantineActive,
  isIsolated,
} from '../core/queries';
import type {
  CareOutcome,
  Decision,
  Evaluation,
  Factor,
  GameState,
  HealthCareOption,
  IllnessId,
  Person,
  PersonId,
  ReadContext,
} from '../core/types';
import { planCare, untreatedDeathChance, type CareSource } from '../phases/bodies/healing';
import { num, valueOf } from '../phases/shared/value';
import { readContext } from './context';

export interface PatientView {
  personId: PersonId;
  /** The illness, or null for an injury alone. */
  illnessId: IllnessId | null;
  injured: boolean;
  /** Seasons left untreated (wasting: fed seasons still needed is shown as remaining). */
  seasonsRemaining: number;
  seasonsSick: number;
  contagious: boolean;
  quarantined: boolean;
  /** Has had a full treatment, so it runs the treated duration. */
  treatedCourse: boolean;
  /** Chance of dying next season if untreated, and the illness's factor list. */
  deathChance: number;
  deathFactors: Factor[];
  /** Chance of dying next season with the planned care. */
  plannedDeathChance: number;
  planned: CareOutcome;
  /** Care received last season, if they were a patient then. */
  lastCare: CareOutcome | null;
  /** The player's mark (draft over standing): true treat first, false do not treat. */
  mark: boolean | null;
  /** 1-based triage position under the drafted policy and marks. */
  rank: number;
}

export interface HealerView {
  personId: PersonId;
  /** Healing slots next season (0 if they cannot work). */
  slots: number;
  working: boolean;
  /** Patients this healer saw last season. */
  treatedLastSeason: PersonId[];
}

export interface HealthOverview {
  policy: HealthCareOption;
  patients: PatientView[];
  healers: HealerView[];
  slots: number;
  capacity: number;
  visits: number;
  medicine: {
    onHand: number;
    capacity: number;
    /** Doses every patient would need next season. */
    demand: number;
    plannedUse: number;
    short: boolean;
  };
  /** Last season's illness multiplier and breakdown, as rolled; null before the first season. */
  lastSeason: {
    turn: number;
    multiplier: number;
    factors: Factor[];
    care: Partial<Record<PersonId, CareOutcome>>;
  } | null;
  /** The multiplier on the current state (between seasons some levers, e.g. crowding, are last season's). */
  current: Evaluation;
  quarantine: {
    active: boolean;
    untilTurn: number | null;
    isolated: number;
    arrivalBoostUntilTurn: number | null;
  };
  sick: number;
  injured: number;
}

function withDraft(state: GameState, draft: Decision | null): GameState {
  if (draft === null) return state;
  const people: GameState['people'] = { ...state.people };
  for (const [id, job] of Object.entries(draft.assignments) as [PersonId, Person['job']][]) {
    const p = people[id];
    if (p !== undefined && p.job !== job) people[id] = { ...p, job };
  }
  return {
    ...state,
    people,
    policies: { ...state.policies, ...draft.policies },
    triageOverrides: { ...state.triageOverrides, ...draft.triageOverrides },
  };
}

function sources(ctx: ReadContext): { sources: CareSource[]; healers: HealerView[] } {
  const out: CareSource[] = [];
  const healers: HealerView[] = [];
  const last = lastCompletedReport(ctx.state);
  const seen = new Map<PersonId, PersonId[]>();
  for (const e of last?.entries ?? []) {
    if (e.kind !== 'treatment' || e.healerId === null) continue;
    seen.set(e.healerId, [...(seen.get(e.healerId) ?? []), e.patientId]);
  }
  for (const p of peopleSorted(ctx.state)) {
    if (p.job !== 'healer') continue;
    const working = isWorking(p);
    const slots = working
      ? Math.max(0, num(ctx, { kind: 'output', personId: p.id, jobId: 'healer' }))
      : 0;
    if (slots > 0) out.push({ id: p.id, slots });
    healers.push({ personId: p.id, slots, working, treatedLastSeason: seen.get(p.id) ?? [] });
  }
  return { sources: out, healers };
}

/** Section 12 overview of health care for the coming season (Health tab). */
export function healthOverview(
  state: GameState,
  draft: Decision | null = null,
  cfg: Config = config,
): HealthOverview {
  const planned = withDraft(state, draft);
  const ctx = readContext(planned, cfg);
  const turn = state.turn + 1;
  const { sources: capacity, healers } = sources(ctx);
  const doses: Partial<Record<PersonId, number>> = {};
  for (const p of peopleSorted(planned)) {
    if (p.status.sick === null && p.status.injured === null) continue;
    doses[p.id] = Math.max(0, num(ctx, { kind: 'demand', personId: p.id, resource: 'medicine' }));
  }
  const plan = planCare(ctx, {
    turn,
    sources: capacity,
    medicine: state.stocks.medicine.amount,
    doses,
  });
  const last = lastCompletedReport(state);
  const careCtx = readContext(
    {
      ...planned,
      currentReport: {
        ...emptySeasonReport(planned),
        needs: last?.needs ?? {},
        care: plan.care,
      },
    },
    cfg,
  );
  const patients: PatientView[] = plan.ranked.map((id, i) => {
    const p = planned.people[id] as Person;
    const sick = p.status.sick;
    const hurt = p.status.injured;
    const main: IllnessId = sick?.illnessId ?? 'injury';
    const death = valueOf(ctx, { kind: 'illnessDeath', personId: id, illnessId: main });
    return {
      personId: id,
      illnessId: sick?.illnessId ?? null,
      injured: hurt !== null,
      seasonsRemaining: Math.max(sick?.seasonsRemaining ?? 0, hurt?.seasonsRemaining ?? 0),
      seasonsSick: sick?.seasonsSick ?? 0,
      contagious: sick !== null && cfg.illnesses[sick.illnessId].contagion > 0,
      quarantined: p.status.quarantined,
      treatedCourse: (sick?.treated ?? false) || (hurt?.treated ?? false),
      deathChance: plan.deathChance[id] ?? 0,
      deathFactors: death.factors,
      plannedDeathChance: untreatedDeathChance(careCtx, p),
      planned: plan.care[id] ?? 'untreated',
      lastCare: last?.care?.[id] ?? null,
      mark: planned.triageOverrides[id] ?? null,
      rank: i + 1,
    };
  });
  let sick = 0;
  let injured = 0;
  let isolated = 0;
  for (const p of peopleSorted(state)) {
    if (p.status.sick !== null) sick++;
    if (p.status.injured !== null) injured++;
    if (isIsolated(cfg, state, p, turn)) isolated++;
  }
  return {
    policy: planned.policies.healthCare,
    patients,
    healers,
    slots: plan.slots,
    capacity: plan.capacity,
    visits: plan.visits.length,
    medicine: {
      onHand: state.stocks.medicine.amount,
      capacity: state.stocks.medicine.capacity,
      demand: plan.medicineDemand,
      plannedUse: plan.medicineUsed,
      short: plan.shortOfMedicine,
    },
    lastSeason:
      last === null
        ? null
        : {
            turn: last.turn,
            multiplier: last.illnessMultiplier,
            factors: last.illnessFactors ?? [],
            care: last.care ?? {},
          },
    current: valueOf(readContext(state, cfg), { kind: 'illnessMultiplier' }),
    quarantine: {
      active: quarantineActive(state, turn),
      untilTurn: state.quarantine.untilTurn,
      isolated,
      arrivalBoostUntilTurn: state.quarantine.contagionBoostUntilTurn,
    },
    sick,
    injured,
  };
}
